import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { ZoomViewer } from "./ZoomViewer";
import { asset } from "@/lib/api";
import { youtubeId } from "@/hooks/useList";
import { EASE } from "./SectionHead";

export const ProjectVideo = ({ url, title }) => {
  const yt = youtubeId(url);
  return (
    <div data-testid="project-video" className="clip-corner bg-[#F5A524]/40 p-px">
      <div className="clip-corner bg-black overflow-hidden">
        {yt ? (
          <iframe title={title} src={`https://www.youtube-nocookie.com/embed/${yt}?rel=0`} allow="encrypted-media; fullscreen" allowFullScreen className="w-full aspect-video border-0" />
        ) : (
          <video src={asset(url)} controls playsInline preload="metadata" className="w-full max-h-[85vh] bg-black" />
        )}
      </div>
    </div>
  );
};

export const ProjectGallery = ({ images, title }) => {
  const [i, setI] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const n = images.length;
  const go = (d) => setI((x) => (x + d + n) % n);

  useEffect(() => {
    if (n < 2 || zoomOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowRight") setI((x) => (x + 1) % n);
      if (e.key === "ArrowLeft") setI((x) => (x - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [n, zoomOpen]);

  return (
    <div data-testid="project-gallery">
      <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.3, delay: 0.2, ease: EASE }}
        className="relative clip-corner overflow-hidden bg-[#12131A] grid h-[70vh] sm:h-[82vh]">
        <AnimatePresence initial={false}>
          <motion.img key={`${images[i]}-${i}`} data-testid="project-image" src={asset(images[i])} alt={`${title} — ${i + 1}`}
            onClick={() => setZoomOpen(true)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
            className="col-start-1 row-start-1 w-full h-full object-contain cursor-zoom-in" />
        </AnimatePresence>
        <button data-testid="gallery-fullscreen-btn" onClick={() => setZoomOpen(true)} aria-label="View full screen"
          className="absolute bottom-4 right-4 flex items-center gap-2 bg-black/75 border border-[#F5A524]/50 px-3 py-2 font-mono text-[11px] tracking-[0.2em] text-[#F5A524] hover:bg-[#F5A524] hover:text-black transition-colors">
          <Maximize2 size={14} /> FULL SCREEN
        </button>
        {n > 1 && (
          <>
            <button data-testid="gallery-prev-btn" onClick={() => go(-1)} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center bg-black/70 text-[#F5A524] hover:bg-[#F5A524] hover:text-black transition-colors"><ChevronLeft /></button>
            <button data-testid="gallery-next-btn" onClick={() => go(1)} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 grid place-items-center bg-black/70 text-[#F5A524] hover:bg-[#F5A524] hover:text-black transition-colors"><ChevronRight /></button>
            <span data-testid="gallery-counter" className="absolute top-4 right-4 bg-black/75 px-3 py-1.5 font-mono text-[11px] tracking-[0.2em] text-[#F5A524]">
              {String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
            </span>
          </>
        )}
      </motion.div>
      {n > 1 && (
        <div data-lenis-prevent className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {images.map((src, k) => (
            <button key={`${src}-${k}`} data-testid={`gallery-thumb-${k}`} onClick={() => setI(k)} aria-label={`Image ${k + 1}`}
              className={`shrink-0 w-20 h-20 sm:w-24 sm:h-24 overflow-hidden border-2 transition-[border-color,opacity] ${k === i ? "border-[#F5A524]" : "border-transparent opacity-50 hover:opacity-100"}`}>
              <img src={asset(src)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {zoomOpen && <ZoomViewer images={images} start={i} title={title} onClose={() => setZoomOpen(false)} />}
    </div>
  );
};
